import {has, isNumber, isString, split, toNumber} from "lodash";
import { isJSON, parseContextVariableValue } from "./dataTypeHelpers";
import { ContextInstanceVariableType, ContextVariableValueType, WebPageComponentType } from "../schemas";
import { useContextStore } from "@/src/stores/useContextStore";
import {DataTypeEnum} from "@/src/shared/models/contextInstance";

export const safeParseJson = (value: unknown, dataType?: DataTypeEnum): any => {
    if (!isJSON(value)) return value
    try {
        const parsedData = JSON.parse(value);
        if ((dataType === DataTypeEnum.STRING) && (isNumber(parsedData))) {
            return value;
        }
        return parsedData;
    } catch (e) {
        return value;
    }
};

export const formatContextVariablesObjectToFormValues = (
  variables: Record<string, ContextInstanceVariableType>,
) => {
  return Object.entries(variables).reduce(
    (acc, [key, { value, dataType }]) => {
      if (key) {
        acc[key] = safeParseJson(value ?? null, dataType);
      }
      return acc;
    },
    {} as Record<string, string | boolean | number | null>,
  );
};

export const formatContextVariablesListToFormValues = (
  variables: ContextInstanceVariableType[],
) => {
  if (!variables) return {};
  return variables.reduce(
    (acc, { value, dataType, key }) => {
      if (key) {
        acc[key] = safeParseJson(value, dataType);
      }
      return acc;
    },
    {} as Record<string, string | boolean | number | null>,
  );
};

export const formatFormDataToVarList = (formData: Record<string, ContextVariableValueType>) => {
  if (!formData) return []
  const varList = [];
  for (const key in formData) {
    const contextVar = useContextStore.getState().getVariable(key);
    if (contextVar) {
      varList.push({ ...contextVar, value: parseContextVariableValue(formData[key]) })
    }
  }
  return varList
};

export const getDefaultValuesFromComponents = (components: WebPageComponentType[]) => {
  return components.reduce((acc, item) => {
    const name = item?.props?.name;
    const defaultValue = item?.props?.defaultValue;

    if (isString(name) && !has(acc, name)) {
      acc[name] = defaultValue;
    }
    return acc;
  }, {} as Record<string, unknown>);
};

export const getFormValues = (
  variables: Record<string, ContextInstanceVariableType>,
  defaultValues: Record<string, unknown>,
  getValues: (name: string) => unknown
) => {
  return Object.keys(variables).reduce((acc, name) => {
    if (!has(acc, name)) {
      const defaultValue = defaultValues[name];
      const currentFormValue = getValues(name);
      const contextVariableValue = variables[name]?.value;
      const valueToParse = currentFormValue ?? contextVariableValue ?? defaultValue ?? null;
      acc[name] = safeParseJson(valueToParse);
    }
    return acc;
  }, {} as Record<string, unknown>);
};

export const getContextVariablesToUpdate = (
    formData: Record<string, ContextInstanceVariableType["value"]>,
    dirtyFields: Record<string, boolean>
  ) => {
    const contextVariablesToUpdate = Object.keys(dirtyFields || {}).reduce(
      (acc, key) => {
        const dirtyFieldValue = dirtyFields[key];
        const contextVariableToUpdate = useContextStore.getState().getVariable(key);

        if (dirtyFieldValue && contextVariableToUpdate) {
          const newValue = parseContextVariableValue(formData[key]);
          if (isString(newValue)) {
            acc.push({ ...contextVariableToUpdate, value: newValue });
          }
        }

        return acc;
      },
      [] as ContextInstanceVariableType[],
    );
    return contextVariablesToUpdate;
  };

export const getClosestNumFromString = (ageRange: string): number | null => {
    return toNumber(split(ageRange, '_').find((item) => !isNaN(toNumber(item))));
};
